import { useEffect, useState, useCallback, useRef } from "react";
import useTranslationHook from "../../hooks/useTranslationHook";

const SCROLL_OFFSET = 50;

const TABS = [
  { id: "description", label: "Description" },
  { id: "reports", label: "Reports & Fees" },
  // { id: "history", label: "Bid History" },
  { id: "map", label: "Map View" },
  { id: "analytics", label: "Analytics Area" },
  { id: "mortgage", label: "Mortgage Calculator" },
  { id: "licenseInformation", label: "License Information" },
  { id: "similar", label: "Similar Properties" },
];

export default function ScrollTabs() {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
    const { t } = useTranslationHook();


  const navRef = useRef(null);
  const headerHeightRef = useRef(0);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!navRef.current || typeof ResizeObserver === "undefined") return;

    const updateHeight = () => {
      headerHeightRef.current =
        navRef.current?.getBoundingClientRect().height || 0;
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(navRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const scrollToSection = useCallback((id) => {
    const section = document.getElementById(id);
    if (!section) return;

    const y =
      section.getBoundingClientRect().top +
      window.scrollY -
      headerHeightRef.current -
      SCROLL_OFFSET;

    window.scrollTo({
      top: Math.max(y, 0),
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (visible?.target?.id) {
          setActiveTab(visible.target.id);
        }
      },
      {
        rootMargin: `-${headerHeightRef.current}px 0px -40% 0px`,
        threshold: 0.1,
      }
    );

    TABS.forEach(({ id }) => {
      const section = document.getElementById(id);
      if (section) observerRef.current.observe(section);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <nav ref={navRef} aria-label="Property Sections">
      <div className="container">
        <ul>
          {TABS.map(({ id, label }) => (
            <li key={id}>
              <button
                type="button"
                className={`tab-button ${activeTab === id ? "active" : ""}`}
                onClick={() => scrollToSection(id)}
                aria-current={activeTab === id ? "page" : undefined}
              >
                {t(label)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}